import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogActionBar } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { RequiredLabel } from '../components/RequiredLabel';
import { toast } from 'sonner';
import { cyclesAPI, restaurantsAPI } from '../lib/api';
import CohortFields from '../components/CohortFields';
import PhasesEditor, { newPhase } from '../components/PhasesEditor';
import { useAuth } from '../context/AuthContext';
import { usePagination, PaginationBar } from '../components/PaginationBar';
import {
  Layers, Plus, ChevronRight, CalendarDays, GraduationCap, Building2,
  CheckCircle2, Clock, AlertTriangle, Search,
} from 'lucide-react';

/**
 * Cycles — centralised management of every placement cycle in the app.
 * List view shows every cycle with roll-ups (students / restaurants /
 * placements / signed contracts / assessment progress). Clicking a row
 * opens the drill-down at /cycles/:id.
 */
const STATUS_STYLE = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  closed: 'bg-blue-100 text-blue-700 border-blue-200',
  archived: 'bg-amber-50 text-amber-800 border-amber-200',
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${STATUS_STYLE[status] || STATUS_STYLE.draft}`}>
    {status}
  </span>
);

const CyclesPage = () => {
  const { isAdmin, isReadOnly } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await cyclesAPI.list();
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load cycles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!query) return true;
      return (r.name || '').toLowerCase().includes(query)
        || String(r.year_of_enrollment).includes(query)
        || (r.year_of_study || '').toLowerCase().includes(query)
        || (r.course_type || '').toLowerCase().includes(query);
    });
  }, [rows, q, statusFilter]);
  const pg = usePagination(filtered, 'cycles');

  const totals = useMemo(() => {
    const acc = { placements: 0, signed: 0, submitted: 0, overdue: 0 };
    for (const r of rows) {
      acc.placements += r.placement_count;
      acc.signed += r.contracts_signed;
      acc.submitted += r.assessments_submitted;
      acc.overdue += r.assessments_overdue;
    }
    return acc;
  }, [rows]);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="cycles-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl font-bold text-ica-navy flex items-center gap-2">
              <Layers className="w-7 h-7" /> Placement Cycles
            </h1>
            <p className="text-gray-500 mt-1">
              Every cohort in one place — a cycle groups the placements, restaurants, contracts and assessments for one class-year and term.
            </p>
          </div>
          {isAdmin && !isReadOnly && (
            <Button
              data-testid="new-cycle-btn"
              onClick={() => setCreateOpen(true)}
              
            >
              <Plus className="w-4 h-4 mr-1" /> New Cycle
            </Button>
          )}
        </div>

        {/* Totals cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Cycles', value: rows.length, Icon: Layers, tone: 'text-ica-navy' },
            { label: 'Placements', value: totals.placements, Icon: GraduationCap, tone: 'text-sky-700' },
            { label: 'Signed contracts', value: totals.signed, Icon: CheckCircle2, tone: 'text-emerald-700' },
            { label: 'Overdue assessments', value: totals.overdue, Icon: AlertTriangle, tone: totals.overdue > 0 ? 'text-red-600' : 'text-gray-500' },
          ].map(({ label, value, Icon, tone }) => (
            <Card key={label} className="border-gray-200">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
                    <p className={`font-heading text-2xl font-bold ${tone}`}>{value}</p>
                  </div>
                  <Icon className={`w-6 h-6 ${tone} opacity-60`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            data-testid="cycles-search"
            placeholder="Search by name, year, course…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-sm"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="cycles-status-filter" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-ica-navy">
              {loading ? 'Loading cycles…' : `${filtered.length} cycle${filtered.length === 1 ? '' : 's'}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <p className="p-6 text-sm text-gray-500 text-center">Loading…</p>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500 text-sm">
                  {rows.length === 0 ? 'No cycles yet. Click "+ New Cycle" to create your first one.' : 'No cycles match your search.'}
                </p>
              </div>
            ) : (
              <>
              <PaginationBar {...pg.bar} position="top" noun="cycles" />
              <ul className="divide-y">
                {pg.pageItems.map((c) => (
                  <li
                    key={c.id}
                    data-testid={`cycle-row-${c.id}`}
                    onClick={() => navigate(`/cycles/${c.id}`)}
                    className="p-4 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 truncate">{c.name}</span>
                        <StatusBadge status={c.status} />
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide">
                          Class of {c.year_of_enrollment} · {c.year_of_study} · {c.course_type}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" /> {c.start_date} → {c.end_date}
                        </span>
                        {c.phase_count > 0 && (
                          <span data-testid={`cycle-phase-count-${c.id}`} className="inline-flex items-center px-1.5 py-0.5 rounded bg-ica-navy/10 text-ica-navy font-semibold text-[10px]">
                            {c.phase_count} phase{c.phase_count === 1 ? '' : 's'}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <GraduationCap className="w-3 h-3" /> {c.student_count} students
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {c.restaurant_count} restaurants
                        </span>
                        <span>· {c.placement_count} placements</span>
                        <span className="text-emerald-700">· {c.contracts_signed}/{c.contracts_signed + c.contracts_pending} signed</span>
                        <span>· {c.assessments_submitted}/{c.assessments_submitted + c.assessments_pending} assessments</span>
                        {c.assessments_overdue > 0 && (
                          <span className="text-red-600 font-semibold">
                            · {c.assessments_overdue} overdue
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-300" />
                  </li>
                ))}
              </ul>
              <PaginationBar {...pg.bar} position="bottom" noun="cycles" />
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <CreateCycleDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
    </Layout>
  );
};

// ---------------------------------------------------------------------------
// Create Cycle — modal wizard: cohort metadata + participating restaurants.
// Cohort roster is live-filtered — students matching the four cohort fields
// are auto-included, so no student picker at create time.
// ---------------------------------------------------------------------------
const CreateCycleDialog = ({ open, onOpenChange, onCreated }) => {
  const emptyForm = {
    name: '',
    year_of_enrollment: '',
    year_of_study: '1st Year',
    course_length: '',
    course_type: '',
    start_date: '',
    end_date: '',
    notes: '',
    restaurant_ids: [],
    default_num_assessments: '',
    phases: [],
  };
  const [form, setForm] = useState(emptyForm);
  const [multiPhase, setMultiPhase] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantQuery, setRestaurantQuery] = useState('');
  const [saving, setSaving] = useState(false);
  // Track which required fields the user has touched so we don't scream red
  // on first paint. On submit we flip `submitAttempted` so ALL required
  // fields get the red-border treatment at once.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setMultiPhase(false);
      setRestaurantQuery('');
      setSubmitAttempted(false);
      restaurantsAPI.getAll().then((r) => setRestaurants(r.data || [])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const suggested = `Class of ${form.year_of_enrollment || '…'} · ${form.year_of_study} · ${form.course_type || 'Course'}`;
    if (!form.name || form.name.startsWith('Class of ')) {
      setForm((f) => ({ ...f, name: suggested }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.year_of_enrollment, form.year_of_study, form.course_type, open]);

  const toggleRestaurant = (id) => {
    setForm((f) => ({
      ...f,
      restaurant_ids: f.restaurant_ids.includes(id)
        ? f.restaurant_ids.filter((x) => x !== id)
        : [...f.restaurant_ids, id],
    }));
  };

  // Live search over the restaurant list (name + cuisine + address).
  const filteredRestaurants = React.useMemo(() => {
    const q = restaurantQuery.trim().toLowerCase();
    if (!q) return restaurants;
    return restaurants.filter((r) =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.cuisine_type || '').toLowerCase().includes(q) ||
      (r.address || '').toLowerCase().includes(q),
    );
  }, [restaurants, restaurantQuery]);

  // In multi-phase mode the cycle window is derived from the phases.
  useEffect(() => {
    if (!multiPhase || form.phases.length === 0) return;
    const starts = form.phases.map((p) => p.start_date).filter(Boolean).sort();
    const ends = form.phases.map((p) => p.end_date).filter(Boolean).sort();
    const start_date = starts[0] || '';
    const end_date = ends[ends.length - 1] || '';
    if (start_date !== form.start_date || end_date !== form.end_date) setForm((f) => ({ ...f, start_date, end_date }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiPhase, form.phases]);

  const toggleMultiPhase = (on) => {
    setMultiPhase(on);
    if (on && form.phases.length === 0) {
      const first = newPhase(0);
      setForm((f) => ({ ...f, phases: [{ ...first, start_date: f.start_date || '', end_date: f.end_date || '', assessment_due_dates: ['', ''] }] }));
    }
    if (!on) setForm((f) => ({ ...f, phases: [] }));
  };

  // Explicit validation with per-field error messages surfaced as toasts +
  // red borders on the offenders. Prevents silent submit failures.
  const validate = () => {
    const problems = [];
    if (!form.name.trim()) problems.push('Cycle name');
    if (!form.course_type.trim()) problems.push('Course type');
    if (!form.year_of_enrollment) problems.push('Year of enrollment');
    if (multiPhase) {
      if (form.phases.length === 0) problems.push('At least one phase');
      form.phases.forEach((p, i) => {
        if (!p.start_date || !p.end_date) problems.push(`Phase ${i + 1} dates`);
        else if (p.end_date < p.start_date) problems.push(`Phase ${i + 1} end before start`);
        if ((p.assessment_due_dates || []).some((d) => !d)) problems.push(`Phase ${i + 1} assessment dates`);
      });
      return problems;
    }
    if (!form.start_date) problems.push('Start date');
    if (!form.end_date) problems.push('End date');
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      problems.push('End date must be on or after start date');
    }
    return problems;
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    const problems = validate();
    if (problems.length > 0) {
      toast.error(`Please fix: ${problems.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_num_assessments: form.default_num_assessments === '' ? null : Number(form.default_num_assessments),
        phases: multiPhase ? form.phases.map((p) => ({ ...p, num_assessments: Number(p.num_assessments) || 0 })) : [],
      };
      const r = await cyclesAPI.create(payload);
      toast.success(`Cycle "${r.data.name}" created`);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create cycle');
    } finally {
      setSaving(false);
    }
  };

  const invalid = (field) => submitAttempted && (
    (field === 'name' && !form.name.trim()) ||
    (field === 'course_type' && !form.course_type.trim()) ||
    (field === 'year_of_enrollment' && !form.year_of_enrollment) ||
    (field === 'start_date' && !form.start_date) ||
    (field === 'end_date' && !form.end_date)
  );
  const invalidCls = 'border-red-500 focus-visible:ring-red-500/40';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-cycle-modal">
        {/* Top action bar — Cancel + Create sit above the form so they're
            always visible without scrolling. Mirrored at the bottom for
            keyboard-heavy admins who hit Tab through the form. */}
        <DialogActionBar>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-cycle-top">Cancel</Button>
          <Button
            data-testid="submit-cycle-top"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Creating…' : 'Create Cycle'}
          </Button>
        </DialogActionBar>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-ica-navy">New Placement Cycle</DialogTitle>
          <DialogDescription className="text-gray-500">
            A cycle groups all placements for one cohort in one term. Students matching the cohort fields will auto-populate the roster.
            Fields marked <span className="text-red-500">*</span> are required.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <CohortFields form={form} setForm={setForm} invalid={invalid} invalidCls={invalidCls} testIdPrefix="cycle" />

          <div className="rounded-md border border-gray-200 bg-gray-50/60 p-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-semibold text-ica-navy">Timeline</div>
                <div className="text-[11px] text-gray-500">Run as one period, or split into phases that can span semesters or academic years.</div>
              </div>
              <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5" data-testid="cycle-timeline-toggle">
                {[[false, 'Single period'], [true, 'Multiple phases']].map(([v, label]) => (
                  <button
                    key={label}
                    type="button"
                    data-testid={v ? 'cycle-timeline-phases' : 'cycle-timeline-single'}
                    onClick={() => toggleMultiPhase(v)}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${multiPhase === v ? 'bg-ica-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {multiPhase && (
              <div className="mt-3">
                <PhasesEditor phases={form.phases} onChange={(phases) => setForm({ ...form, phases })} testIdPrefix="cycle-phase" />
              </div>
            )}
          </div>

          {!multiPhase && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <RequiredLabel>Start date</RequiredLabel>
              <Input
                data-testid="cycle-start-date"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className={invalid('start_date') ? invalidCls : ''}
              />
            </div>
            <div>
              <RequiredLabel>End date</RequiredLabel>
              <Input
                data-testid="cycle-end-date"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className={invalid('end_date') ? invalidCls : ''}
              />
            </div>
          </div>
          )}

          <div>
            <RequiredLabel>Cycle name</RequiredLabel>
            <Input
              data-testid="cycle-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={invalid('name') ? invalidCls : ''}
            />
          </div>

          {/* Participating restaurants — with search. */}
          <div>
            <Label>Participating restaurants ({form.restaurant_ids.length} selected)</Label>
            <div className="relative mt-1">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="cycle-restaurant-search"
                placeholder="Search by name, cuisine or address…"
                value={restaurantQuery}
                onChange={(e) => setRestaurantQuery(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
            <div className="max-h-48 overflow-y-auto border rounded p-2 space-y-1 mt-2 bg-gray-50" data-testid="cycle-restaurant-list">
              {restaurants.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No restaurants yet</p>
              ) : filteredRestaurants.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No restaurants match &quot;{restaurantQuery}&quot;</p>
              ) : (
                filteredRestaurants.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white rounded px-1.5 py-1">
                    <input
                      type="checkbox"
                      data-testid={`cycle-restaurant-${r.id}`}
                      checked={form.restaurant_ids.includes(r.id)}
                      onChange={() => toggleRestaurant(r.id)}
                    />
                    <span className="truncate flex-1">{r.name}</span>
                    <span className="text-[10px] text-gray-400 shrink-0">{r.cuisine_type || ''}</span>
                    <span className="text-[10px] text-gray-500 shrink-0">{r.available_positions} pos</span>
                  </label>
                ))
              )}
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Restaurants can be added/removed later from the cycle drill-down.</p>
          </div>

          {!multiPhase && (
          <div>
            <Label>Default number of assessments (optional)</Label>
            <Input
              data-testid="cycle-num-assessments"
              type="number" min={0} max={20}
              value={form.default_num_assessments}
              onChange={(e) => setForm({ ...form, default_num_assessments: e.target.value })}
              placeholder="Leave empty to fall back to Assessment Rules"
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Applied to new placements added to this cycle. You can still override per placement, and set individual due dates on the cycle&apos;s Settings tab after creation.
            </p>
          </div>
          )}

          <div>
            <Label>Notes (optional)</Label>
            <Input data-testid="cycle-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-cycle-bottom">Cancel</Button>
          <Button
            data-testid="submit-cycle-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Creating…' : 'Create Cycle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CyclesPage;
